import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import BoonLogo from '../../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../../assets/images/BoonViewIcon.svg';
import { ReactComponent as SuccessIcon } from '../../../assets/images/referral-success.svg';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import { OpeningSelect } from '../../../components/OpeningSelect/OpeningSelect';
import { OrganizationSelect } from '../../../components/OrganizationSelect/OrganizationSelect';
import { ReferralProgressBar } from '../../../components/ReferralProgressBar/ReferralProgressBar';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { CandidateInfoSection } from '../../../components/shared/CandidateInfoSection/CandidateInfoSection';
import Header from '../../../components/shared/Header/Header';
import { useReferralFlowContext } from '../../../contexts/ReferralFlowContext';
import { IsMobile } from '../../../helpers/IsMobile';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useGetReferralQuery } from '../../../redux/apis/referralApi';
import urls from '../../../routing/urls';

import styles from './Success.module.scss';

const calculateApplicationStrength = (referRequest, strengthenQuestions) => {
  let answered = 0;
  let total = 0;

  const check = (enabled, hasValue) => {
    if (enabled) {
      total += 1;
      if (hasValue) answered += 1;
    }
  };

  // Parse links once
  let firstLink = null;
  try {
    const links = referRequest?.request_link_url_json ? JSON.parse(referRequest.request_link_url_json) : [];
    if (Array.isArray(links) && links.length > 0) {
      [firstLink] = links;
    }
  } catch (e) {
    firstLink = null;
  }

  check(strengthenQuestions.how_good_are_they, (referRequest?.skills?.length || 0) > 0);
  check(strengthenQuestions.know_the_person_level, !!referRequest?.know_the_person_level);
  check(strengthenQuestions.worked_with_person, !!referRequest?.worked_with_person);
  check(strengthenQuestions.open_for_new_opportunities_status, !!referRequest?.open_for_opportunities_status);
  // Diversity hire: only counts as answered when actively true (box ticked).
  check(strengthenQuestions.diversity_hire, referRequest?.diversity_hire === true);
  check(
    strengthenQuestions.attach_url,
    !!firstLink &&
      firstLink.type === 'ln' &&
      firstLink.value !== 'www.linkedin.com/in/' &&
      firstLink.value !== ''
  );
  check(strengthenQuestions.attach_file, (referRequest?.request_pdf_files?.length || 0) > 0);

  // Baseline strength: every referral/application starts at 17%. The remaining
  // 83% is earned by answering enabled questions and added on top of the base.
  const BASE_STRENGTH = 17;

  if (total === 0) {
    return BASE_STRENGTH;
  }

  return Math.min(100, BASE_STRENGTH + Math.round((answered / total) * (100 - BASE_STRENGTH)));
};

export const Success = () => {
  const { t } = useTranslation('referral');
  const navigate = useNavigate();
  const { isAuthenticated } = useCurrentUser();
  const { companyName, referralId } = useParams();
  const { clearReferralFlowData, strengthenQuestions } = useReferralFlowContext();
  // Progress bar is only meaningful when the master toggle is on AND at least one
  // individual question is enabled in Admin Web. Otherwise, hide it entirely.
  const hasAnyReferralQuestionEnabled =
    !!strengthenQuestions.enable_referral_questions &&
    !!(
      strengthenQuestions.how_good_are_they ||
      strengthenQuestions.know_the_person_level ||
      strengthenQuestions.worked_with_person ||
      strengthenQuestions.open_for_new_opportunities_status ||
      strengthenQuestions.diversity_hire ||
      strengthenQuestions.attach_url ||
      strengthenQuestions.attach_file
    );
  const { data: referral, isFetching: isReferralFetching } = useGetReferralQuery({ referralId });
  const referRequest = referral?.payload?.refer_request;
  const isMobile = IsMobile();
  const applicationStrength = referRequest ? calculateApplicationStrength(referRequest, strengthenQuestions) : 17;

  const candidateInfo = {
    first_name: referRequest?.name?.split(' ')[0] || '',
    last_name: referRequest?.name?.split(' ')[1] || '',
    email: referRequest?.email || '',
    phone_number: referRequest?.phone_number?.full_number || '',
    linkedin_url: referRequest?.linkedin_link || '',
  };

  const customQuestions = referRequest?.custom_questions || [];

  const handleSendAnotherReferral = () => {
    clearReferralFlowData();

    const url = companyName ? urls.companyReferral : urls.refer;
    navigate(url.replace(':companyName', companyName));
  };

  const handleViewInTracker = () => {
    clearReferralFlowData();
    navigate(urls.tracker);
  };

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className="w-full h-[100vh]">
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('headerTitle')}
          backButton={isAuthenticated && !companyName ? t('common:back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.outerContainer}>
          <div className={styles.container}>
            <div className={styles.formSection}>
              <div className={styles.formHeader}>
                <h2>{t('sendAReferral')}</h2>
              </div>
              <div className={styles.successContainer}>
                <SuccessIcon />
                <div className={styles.successTextContainer}>
                  <p className={styles.successTextTitle}>{t('successPage.title')}</p>
                  <p className={styles.successTextDescription}>{t('successPage.description')}</p>
                </div>
              </div>

              <p className={styles.referralSummaryTitle}>{t('successPage.referralSummary')}</p>
              <div className={styles.previousInfo}>
                <CandidateInfoSection
                  candidateInfo={candidateInfo}
                  isEditable={false}
                  isLoading={isReferralFetching}
                  customQuestions={customQuestions}
                />
                {!isAuthenticated && !companyName && (
                  <div className="space-y-[6px]">
                    <label htmlFor="company" className={styles.formLabel}>
                      {t('company')}
                    </label>
                    <OrganizationSelect value={referRequest?.organization_id} isDisabled />
                  </div>
                )}
                <div className="space-y-[6px]">
                  <label htmlFor="opening" className={styles.formLabel}>
                    {t('openingInfo')}
                  </label>
                  <OpeningSelect
                    organizationId={referRequest?.organization_id}
                    value={referRequest?.job_id}
                    isDisabled
                  />
                </div>
              </div>

              <div className={styles.nextButtonContainer}>
                <ButtonWithIcon
                  type="button"
                  variant="tertiary-36"
                  text={t('successPage.sendAnother')}
                  textClassName="text-[#314155]"
                  onClick={handleSendAnotherReferral}
                />
                <ButtonWithIcon
                  variant="secondary-36"
                  text={t('successPage.viewInTracker')}
                  textClassName="text-white"
                  onClick={handleViewInTracker}
                />
              </div>
            </div>
          </div>
          {hasAnyReferralQuestionEnabled && (
            <div className={styles.applicationStrengthContainer}>
              <ReferralProgressBar
                title={t('successPage.applicationStrength')}
                applicationStrength={applicationStrength}
              />
              <div className={styles.applicationStrengthDescriptionContainer}>
                <p className={styles.applicationStrengthDescription}>
                  {applicationStrength === 100 ? t('successPage.greatJob') : t('successPage.notStrong')}
                </p>
              </div>
              {applicationStrength !== 100 && (
                <ButtonWithIcon
                  type="button"
                  variant="tertiary-36"
                  text={t('successPage.makeIt100')}
                  containerClass="self-end"
                  textClassName="text-[#314155]"
                  onClick={() => {
                    if (companyName) {
                      navigate(
                        urls.companyReferralStrengthen
                          .replace(':companyName', companyName)
                          .replace(':referralId', referralId)
                      );
                    } else {
                      navigate(urls.referralFlowStrengthen.replace(':referralId', referralId));
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
